"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.adjustPointsSchema = exports.cardTapSchema = exports.balanceRefundSchema = exports.refillSchema = exports.customerListQuerySchema = exports.updateCustomerSchema = exports.createCustomerSchema = exports.refundOrderSchema = void 0;
exports.resolveRefillBonus = resolveRefillBonus;
exports.customerDiscountAmount = customerDiscountAmount;
exports.computeBucketDiscounts = computeBucketDiscounts;
exports.computeBucketDiscountsWithAmounts = computeBucketDiscountsWithAmounts;
const zod_1 = require("zod");
const money_1 = require("./money");
const checkout_1 = require("./checkout");
/** Profile → Order History → Refund Order: which lines, how many of each. */
exports.refundOrderSchema = zod_1.z.object({
    items: zod_1.z
        .array(zod_1.z.object({
        orderItemId: zod_1.z.string().min(1),
        quantity: zod_1.z.number().int().min(1),
    }))
        .min(1, 'Pick at least one item to refund.'),
});
/* -------------------------------------------------------------------------- */
/* Write models                                                               */
/* -------------------------------------------------------------------------- */
/**
 * Names are routinely Arabic or Kurdish, so every name field is plain text —
 * never transliterated, slugged or case-folded.
 */
const fullName = zod_1.z.string().trim().min(1, 'A name is required.').max(120);
/**
 * Phone numbers are stored exactly as typed. No normalisation: Iraqi numbers
 * are written locally (`0770…`) and internationally (`+964770…`), staff type
 * whichever the customer says, and rewriting one into the other would make the
 * number the customer recognises un-searchable.
 */
const phone = zod_1.z.string().trim().max(40);
/** RFID UIDs are hex from the reader; case is normalised so a tap always matches. */
const cardId = zod_1.z
    .string()
    .trim()
    .min(1)
    .max(64)
    .transform((value) => value.toUpperCase());
exports.createCustomerSchema = zod_1.z.object({
    fullName,
    phone: phone.optional(),
    email: zod_1.z.string().trim().email('Enter a valid email.').max(160).optional(),
    /** Portal login name. Unique per venue; omit for a cashier-only account. */
    username: zod_1.z.string().trim().min(3).max(40).optional(),
    /** Sets a portal password. Hashed server-side with Argon2id (§11). */
    password: zod_1.z.string().min(6).max(200).optional(),
    cardId: cardId.optional(),
    /**
     * DEPRECATED (wave 3) — kept ONLY so older callers don't 400. Written to
     * the dead column and read by nothing; it does NOT seed the buckets (the
     * migration seeded existing rows once). Use discountTimePercent.
     */
    discountPercent: zod_1.z.number().int().min(0).max(100).default(0),
    discountTimePercent: zod_1.z.number().int().min(0).max(100).default(0),
    discountCafePercent: zod_1.z.number().int().min(0).max(100).default(0),
    discountKitchenPercent: zod_1.z.number().int().min(0).max(100).default(0),
    discountShishaPercent: zod_1.z.number().int().min(0).max(100).default(0),
    note: zod_1.z.string().trim().max(500).optional(),
});
/**
 * Everything except balance and points is editable here.
 *
 * Balance is deliberately absent: it moves only through a Refill, a Balance
 * Refund or a payment, each of which writes its own audited row. A settable
 * balance field would be an unaudited way to mint money.
 */
exports.updateCustomerSchema = zod_1.z
    .object({
    fullName: fullName.optional(),
    phone: phone.nullable().optional(),
    email: zod_1.z.string().trim().email('Enter a valid email.').max(160).nullable().optional(),
    username: zod_1.z.string().trim().min(3).max(40).nullable().optional(),
    password: zod_1.z.string().min(6).max(200).optional(),
    cardId: cardId.nullable().optional(),
    discountPercent: zod_1.z.number().int().min(0).max(100).optional(),
    discountTimePercent: zod_1.z.number().int().min(0).max(100).optional(),
    discountCafePercent: zod_1.z.number().int().min(0).max(100).optional(),
    discountKitchenPercent: zod_1.z.number().int().min(0).max(100).optional(),
    discountShishaPercent: zod_1.z.number().int().min(0).max(100).optional(),
    note: zod_1.z.string().trim().max(500).nullable().optional(),
    isActive: zod_1.z.boolean().optional(),
})
    .refine((value) => Object.keys(value).length > 0, { message: 'Nothing to change.' });
/** SPEC §5.5 search: name, phone or card, plus the usual paging. */
exports.customerListQuerySchema = zod_1.z.object({
    /** Matches full name, phone or card id. */
    search: zod_1.z.string().trim().max(80).optional(),
    includeInactive: zod_1.z.coerce.boolean().default(false),
    /** Only accounts holding a balance — the "balances outstanding" report cut (§5.7). */
    withBalanceOnly: zod_1.z.coerce.boolean().default(false),
    sort: zod_1.z.enum(['name', 'balance', 'points', 'recent']).default('name'),
    page: zod_1.z.coerce.number().int().min(1).default(1),
    pageSize: zod_1.z.coerce.number().int().min(1).max(500).default(50),
});
/**
 * Take a deposit (§5.5).
 *
 * The amount is whole IQD because it is physical cash: the venue does not
 * handle sub-dinar notes, and letting a fractional deposit through would put a
 * balance on the account that can never be paid back out exactly.
 */
exports.refillSchema = zod_1.z
    .object({
    amountIqd: zod_1.z
        .number()
        .int('Enter a whole amount.')
        .min(1, 'Enter an amount greater than zero.')
        .max(1_000_000_000),
    note: zod_1.z.string().trim().max(300).optional(),
    /**
     * How the deposit was paid (2026-09-24). Cash, or an electronic payment
     * through a configured provider. Never ACCOUNT_BALANCE: a refill paid from
     * the balance it refills is not a deposit. Optional so a staff tab built
     * before this sends nothing and is read as cash, which is all it could take.
     */
    method: zod_1.z.enum(['CASH', 'ELECTRONIC']).optional(),
    /** Required exactly when `method` is ELECTRONIC — the shared tender rule. */
    electronicsProviderId: zod_1.z.string().min(1).optional(),
})
    .refine(checkout_1.electronicProviderRuleHolds, {
    message: checkout_1.ELECTRONIC_PROVIDER_RULE_MESSAGE,
    path: ['electronicsProviderId'],
});
/**
 * Pay remaining balance back out (§5.5 "Balance Refund").
 *
 * Allowed ONLY when VenueSettings "Enable Balance Refund" is on, and only with
 * `customers.refundBalance`. A reason is mandatory: this takes cash out of the
 * drawer against a balance the venue already banked, so the audit entry is the
 * control on it.
 */
exports.balanceRefundSchema = zod_1.z.object({
    amountIqd: zod_1.z
        .number()
        .int('Enter a whole amount.')
        .min(1, 'Enter an amount greater than zero.')
        .max(1_000_000_000),
    reason: zod_1.z.string().trim().min(1, 'A reason is required.').max(300),
});
/** An RFID tap at a PC or at the cashier (§5.5 "Login with card"). */
exports.cardTapSchema = zod_1.z.object({
    cardId,
    /** Which machine read the card, when a device initiated the tap. */
    deviceId: zod_1.z.string().min(1).optional(),
});
/** Manual points correction. Signed; a reason is required because it is audited. */
exports.adjustPointsSchema = zod_1.z.object({
    points: zod_1.z
        .number()
        .int()
        .refine((value) => value !== 0, 'Enter a non-zero adjustment.')
        .refine((value) => Math.abs(value) <= 1_000_000, 'That adjustment is too large.'),
    reason: zod_1.z.string().trim().min(1, 'A reason is required.').max(300),
});
/**
 * Resolve the recharge bonus for a deposit (§5.5).
 *
 * The rule, stated once: **the highest matching tier wins; with no matching
 * tier the flat percentage applies.** A tier matches when the deposit is at or
 * above its threshold, so "deposit 50,000 -> +10%" is a tier with
 * `minAmountMd = 50_000_000` and `bonusPercent = 10`.
 *
 * Tiers are NOT additive with the flat rate. Adding them would mean a venue
 * that sets a 5% flat rate and then adds a 10% tier silently pays 15% at the
 * top — which is not what "deposit 50,000, receive 10% extra" says, and is the
 * kind of thing nobody notices until the month-end numbers are wrong.
 *
 * The bonus is rounded to a whole IQD with the venue's rule when one is given,
 * so a customer's balance never carries a sub-dinar remainder they cannot spend
 * or be paid back. That is the single rounding step for this transaction; the
 * deposit itself is already whole IQD.
 */
function resolveRefillBonus(amount, config) {
    const none = {
        bonusMd: money_1.ZERO_MD,
        bonusPercentApplied: 0,
        matchedTierMinAmountMd: null,
    };
    // A payout or a zero deposit earns nothing. Guarding here rather than at the
    // call site keeps a negative amount from minting a negative "bonus".
    if (amount <= 0n)
        return none;
    let matched = null;
    for (const tier of config.tiers) {
        if (tier.isActive === false)
            continue;
        const threshold = (0, money_1.fromMillidinars)(tier.minAmountMd);
        if (threshold > amount)
            continue;
        if (matched === null || threshold > matched.minAmountMd) {
            matched = { minAmountMd: threshold, bonusPercent: tier.bonusPercent };
        }
    }
    const percent = matched ? matched.bonusPercent : config.flatPercent;
    if (!Number.isFinite(percent) || percent <= 0) {
        return { ...none, matchedTierMinAmountMd: matched ? matched.minAmountMd.toString() : null };
    }
    const raw = (0, money_1.percentage)(amount, percent);
    return {
        bonusMd: config.rounding ? (0, money_1.applyRounding)(raw, config.rounding) : raw,
        bonusPercentApplied: percent,
        matchedTierMinAmountMd: matched ? matched.minAmountMd.toString() : null,
    };
}
/**
 * The per-account discount (§5.5) as an amount off a given subtotal.
 *
 * Never rounded here: this is one line of a bill whose total is rounded once,
 * at close (§4.1). Rounding the discount too would round the same bill twice.
 */
function customerDiscountAmount(subtotal, discountPercent) {
    if (!Number.isFinite(discountPercent) || discountPercent <= 0)
        return money_1.ZERO_MD;
    return (0, money_1.percentage)(subtotal, Math.min(discountPercent, 100));
}
/**
 * The split discount (§5.5): Time + Cafe + Kitchen, and Shisha since wave 9.
 *
 * THE one function both sides call — the checkout that charges and every
 * preview that promises. Per-bucket integer floor via {@link percentage}
 * (which truncates toward zero on BigInt division), summed WITHOUT rounding:
 * the bill's total is rounded once at close, and a discount that rounded
 * itself would round the same money twice. Percents are clamped to [0, 100]
 * and anything non-finite is treated as 0 — a corrupt rate must shrink the
 * discount, never the charge below zero or above the base.
 */
function computeBucketDiscounts(bases, percents) {
    const timeDiscountMd = customerDiscountAmount(bases.timeBaseMd, percents.timePercent);
    const cafeDiscountMd = customerDiscountAmount(bases.cafeBaseMd, percents.cafePercent);
    const kitchenDiscountMd = customerDiscountAmount(bases.kitchenBaseMd, percents.kitchenPercent);
    const shishaDiscountMd = customerDiscountAmount(bases.shishaBaseMd ?? money_1.ZERO_MD, percents.shishaPercent ?? 0);
    return {
        timeDiscountMd,
        cafeDiscountMd,
        kitchenDiscountMd,
        shishaDiscountMd,
        totalDiscountMd: (0, money_1.add)((0, money_1.add)((0, money_1.add)(timeDiscountMd, cafeDiscountMd), kitchenDiscountMd), shishaDiscountMd),
    };
}
/**
 * A bucket discount that is BOTH a percent and a typed amount (owner decision:
 * "both, side by side").
 *
 * The percent is the customer's standing arrangement, or whatever the cashier
 * overrode it to for this one checkout; the amount is what gets haggled at the
 * counter. They add.
 *
 * Each bucket is then CLAMPED AT ITS OWN BASE, and that clamp is the whole point
 * of this function existing rather than callers adding two numbers. Without it,
 * 5,000 typed off a 3,000 cafe tab makes the cafe bucket owe the customer money,
 * and `allocateProportionally` — which refuses a negative total outright — is the
 * next thing that sees it. A discount reduces what is owed to zero; going past
 * zero is a refund, and a refund is not a discount.
 *
 * The overall manual discount stays outside this: it spreads across every bill
 * and is judged against the grand total, where negative sales are a deliberate,
 * separately-gated venue setting.
 */
function computeBucketDiscountsWithAmounts(bases, percents, amounts) {
    const fromPercents = computeBucketDiscounts(bases, percents);
    const capped = (base, discount) => {
        const floor = (0, money_1.clampToZero)(base);
        return discount > floor ? floor : (0, money_1.clampToZero)(discount);
    };
    const timeDiscountMd = capped(bases.timeBaseMd, (0, money_1.add)(fromPercents.timeDiscountMd, amounts.timeMd));
    const cafeDiscountMd = capped(bases.cafeBaseMd, (0, money_1.add)(fromPercents.cafeDiscountMd, amounts.cafeMd));
    const kitchenDiscountMd = capped(bases.kitchenBaseMd, (0, money_1.add)(fromPercents.kitchenDiscountMd, amounts.kitchenMd));
    const shishaDiscountMd = capped(bases.shishaBaseMd ?? money_1.ZERO_MD, (0, money_1.add)(fromPercents.shishaDiscountMd, amounts.shishaMd ?? money_1.ZERO_MD));
    return {
        timeDiscountMd,
        cafeDiscountMd,
        kitchenDiscountMd,
        shishaDiscountMd,
        totalDiscountMd: (0, money_1.add)((0, money_1.add)((0, money_1.add)(timeDiscountMd, cafeDiscountMd), kitchenDiscountMd), shishaDiscountMd),
    };
}
//# sourceMappingURL=customers.js.map